import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLicenseStatistics } from "@/hooks/use-licenses";
import { useMyOrganizations } from "@/hooks/use-users";
import { toast } from "@/lib/instrumented-toast";
import { formatUserName } from "@/lib/utils";
import { useApiInstance } from "@/providers/api-provider";
import { IstariUserProfile, useUserStore } from "@/stores/user-store";
import { ArrowLeftRight, Container, Key, LogOut, MoreVertical, UserPen } from "lucide-react";
import React from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";

export function NavUser() {
  const { isMobile } = useSidebar();
  const auth = useAuth();
  const navigate = useNavigate();
  const user = (useUserStore.use.user() as IstariUserProfile) || {};
  const setUser = useUserStore.use.setUser();
  const setToken = useUserStore.use.setToken();
  const [editProfileOpen, setEditProfileOpen] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [editingProfile, setEditingProfile] = React.useState(false);

  const [editedFirstName, setEditedFirstName] = React.useState("");
  const [editedLastName, setEditedLastName] = React.useState("");
  const [editedDisplayName, setEditedDisplayName] = React.useState("");

  const licenseStats = useLicenseStatistics(user?.defaultOrg?.id);
  const orgsQuery = useMyOrganizations();

  const profileData = {
    first_name: editingProfile ? editedFirstName : (user.given_name as string) || "",
    last_name: editingProfile ? editedLastName : (user.family_name as string) || "",
    display_name: editingProfile ? editedDisplayName : (user.name as string) || "",
  };

  const sortRolesByPrivilege = (roles: string[]) => {
    const order = ["SUPERADMIN", "ORGOWNER", "ORGEDITOR", "ORGDEVELOPER", "ORGFINANCE", "ORGVIEWER"];
    return roles.sort((a, b) => {
      const indexA = order.indexOf(a);
      const indexB = order.indexOf(b);
      return (indexA === -1 ? order.length : indexA) - (indexB === -1 ? order.length : indexB);
    });
  };

  const handleOpenLicenses = () => {
    setEditProfileOpen(false);
    navigate("/licenses");
  };

  const handleOpenEditProfile = () => {
    setEditProfileOpen(true);
    setDropdownOpen(false);
    setEditedFirstName((user.given_name as string) || "");
    setEditedLastName((user.family_name as string) || "");
    setEditedDisplayName((user.name as string) || "");
    setEditingProfile(false);
  };

  const logout = () => {
    auth.signoutRedirect();
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await useApiInstance.patch("/api/v1/users/me/profile", profileData);
      toast.success("Profile updated successfully");
      setEditProfileOpen(false);
      try {
        const refreshedUser = await auth.signinSilent();
        if (refreshedUser?.access_token) {
          setToken(refreshedUser.access_token);
          setUser(refreshedUser.profile as IstariUserProfile);
        }
      } catch (refreshError) {
        console.error("Session refresh failed after profile update:", refreshError);
        toast.error("Profile updated but session refresh failed. Please reload the page.");
      }
    } catch (error) {
      toast.error(`Failed to update profile: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="hover:bg-transparent hover:text-inherit focus-visible:ring-0">
              <Avatar className="h-8 w-8 rounded-lg grayscale">
                <AvatarFallback className="rounded-lg">{formatUserName(user.name)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs text-muted-foreground">{user.email}</span>
              </div>
              <MoreVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div
                className="flex cursor-pointer items-center gap-2 px-1 py-1.5 text-left text-sm"
                onClick={handleOpenEditProfile}
                data-testid="profile-menu-item"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">{formatUserName(user.name)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                </div>
                <UserPen className="h-4 w-4 text-muted-foreground" />
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  setDropdownOpen(false);
                  navigate("/organization-tokens");
                }}
                className="focus:bg-transparent focus:text-inherit"
                style={{ cursor: "pointer" }}
              >
                <Key />
                Organization Access Tokens
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setDropdownOpen(false);
                  navigate("/jfrog-tokens");
                }}
                className="focus:bg-transparent focus:text-inherit"
                style={{ cursor: "pointer" }}
              >
                <Container />
                JFrog Registry Tokens
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  auth.signinRedirect({
                    state: {
                      from: window.location.pathname + window.location.search,
                    },
                  })
                }
                className="focus:bg-transparent focus:text-inherit"
                style={{ cursor: "pointer" }}
              >
                <ArrowLeftRight />
                Switch Account
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="focus:bg-transparent focus:text-inherit"
              style={{ cursor: "pointer" }}
            >
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <Sheet open={editProfileOpen} onOpenChange={setEditProfileOpen}>
        <SheetContent className="flex flex-col" hideClose>
          <SheetHeader className="sr-only">
            <SheetTitle>Profile</SheetTitle>
            <SheetDescription>View and edit your profile details and organization memberships.</SheetDescription>
          </SheetHeader>
          <div className="grid flex-1 auto-rows-min gap-4 overflow-y-auto px-4 py-4">
            {/* Profile Details Section */}
            <div className="grid gap-3 border-b pb-3">
              <div className="mb-1 flex items-center justify-between">
                <Label className="text-base font-semibold">Profile Details</Label>
                {!editingProfile && (
                  <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)}>
                    Edit Profile
                  </Button>
                )}
              </div>
              {!editingProfile ? (
                <div className="grid gap-2">
                  <div className="grid gap-1">
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <div className="text-sm">{user.name}</div>
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <div className="text-sm">{user.email}</div>
                  </div>
                </div>
              ) : (
                <div className="grid gap-2">
                  <div className="grid gap-1">
                    <Label htmlFor="edit-first-name" className="text-xs">
                      First Name
                    </Label>
                    <Input
                      id="edit-first-name"
                      value={editedFirstName}
                      onChange={(e) => setEditedFirstName(e.target.value)}
                      placeholder="Enter first name..."
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="edit-last-name" className="text-xs">
                      Last Name
                    </Label>
                    <Input
                      id="edit-last-name"
                      value={editedLastName}
                      onChange={(e) => setEditedLastName(e.target.value)}
                      placeholder="Enter last name..."
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="edit-display-name" className="text-xs">
                      Display Name
                    </Label>
                    <Input
                      id="edit-display-name"
                      value={editedDisplayName}
                      onChange={(e) => setEditedDisplayName(e.target.value)}
                      placeholder="Enter display name..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingProfile(false)} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveProfile} disabled={isSaving}>
                      {isSaving ? "Saving..." : "Save Profile"}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Organization Section (Read-only) */}
            <div className="grid gap-2 border-b pb-3">
              <Label className="text-sm font-medium">Organization</Label>
              {user.organizations && user.organizations.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {Array.from(new Set(user.organizations))
                    .slice(0, 3)
                    .map((orgId) => {
                      const org = orgsQuery.data?.find((o) => o.id === orgId);
                      const orgName = org?.name || (orgId === user.defaultOrg?.id ? user.defaultOrg?.name : null);
                      const isPrimary = orgId === user.defaultOrg?.id;

                      if (!orgName) {
                        return null;
                      }

                      return (
                        <TooltipProvider key={orgId}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="cursor-help text-xs">
                                {orgName}
                                {isPrimary && <span className="ml-1">★</span>}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="space-y-1">
                                <p className="font-medium">{orgName}</p>
                                <p className="text-xs text-muted-foreground">ID: {orgId}</p>
                                {isPrimary && <p className="text-xs">★ Primary organization</p>}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  {Array.from(new Set(user.organizations)).length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{Array.from(new Set(user.organizations)).length - 3}
                    </Badge>
                  )}
                </div>
              ) : (
                <Badge variant="outline" className="w-fit text-xs">
                  {user.defaultOrg?.name || "No Organization"}
                </Badge>
              )}
            </div>

            {/* Roles Section (Read-only) */}
            <div className="grid gap-2 border-b pb-3">
              <Label className="text-sm font-medium">Roles</Label>
              <div className="mb-1 text-xs text-muted-foreground">Roles shown are for your primary organization</div>
              <div className="flex flex-wrap gap-1">
                {user.roleKeys &&
                  sortRolesByPrivilege(user.roleKeys).map((role) => (
                    <Badge key={role} variant={role === "SUPERADMIN" ? "destructive" : "outline"} className="text-xs">
                      {role.replace("ORG", "")}
                    </Badge>
                  ))}
              </div>
            </div>

            {/* Organization Access Tokens Section */}
            <div className="grid gap-2 border-b pb-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Organization Access Tokens</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditProfileOpen(false);
                    navigate("/organization-tokens");
                  }}
                >
                  Manage
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Generate and manage tokens for the Customer Portal API</p>
            </div>

            {/* JFrog Registry Tokens Section */}
            <div className="grid gap-2 border-b pb-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">JFrog Registry Tokens</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditProfileOpen(false);
                    navigate("/jfrog-tokens");
                  }}
                >
                  Manage
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                View registry credentials, expiration dates, and manage tokens for pulling images
              </p>
            </div>

            {/* License Count Section (Read-only) */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Licenses</Label>
                <Button variant="outline" size="sm" onClick={handleOpenLicenses}>
                  Manage
                </Button>
              </div>
              {licenseStats.isLoading ? (
                <div className="text-sm text-muted-foreground">Loading license information...</div>
              ) : licenseStats.error ? (
                <div className="text-sm text-muted-foreground">Unable to load license information</div>
              ) : (
                <div className="grid gap-2">
                  <div className="flex items-center justify-between rounded-md border p-2">
                    <span className="text-xs text-muted-foreground">Total Licenses</span>
                    <span className="font-medium">{licenseStats.data?.total_licenses || 0}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-2">
                    <span className="text-xs text-muted-foreground">Active Licenses</span>
                    <span className="font-medium">{licenseStats.data?.active_licenses || 0}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditProfileOpen(false)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </SidebarMenu>
  );
}
